
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import ManagePresets from "./ManagePresets.vue";
import EditPreset from "./EditPreset.vue";
import { globalStore, uiStore } from "@/store/store";
import { closeUiPanel } from "@/lib/panels/panels";
import { ClosingConfirmationChosenOption } from "@/types/ui";
import {
  refetchPresetsFromServerToGlobalStore,
  savePresetsToWordPress
} from "@/lib/presets/presets";

@Component({
  components: {
    EditPreset,
    ManagePresets
  }
})
export default class PresetsPanel extends Vue {
  isLoadingData = false;
  isStoringData = false;

  get activePreset() {
    return uiStore.activePreset;
  }

  async beforeMount() {
    if (uiStore.panelState.shouldLoadFreshDataIntoStoreOnceOpened.presets) {
      uiStore.setPanelState({
        ...uiStore.panelState,
        presetsPanelActiveViewMode: "controls"
      });
      await this.load();
    } else {
      uiStore.setShouldPanelLoadFreshDataIntoStoreOnceOpened("presets", true);
    }
  }

  async load() {
    this.isLoadingData = true;
    const isSuccessful = await refetchPresetsFromServerToGlobalStore();
    this.isLoadingData = false;

    return isSuccessful;
  }

  async save() {
    this.isStoringData = true;
    const isSuccessful = await savePresetsToWordPress();
    this.isStoringData = false;

    return isSuccessful;
  }

  requestPanelClosingConfirmation() {
    uiStore.requestClosingConfirmation({
      subject: "presets",
      confirmationDefinition: {
        title: "You have unsaved changes to your Presets.",
        text: "Would you like to save or discard these changes?",
        type: "info"
      }
    });
  }

  get unsavedChangesPresent(): boolean {
    return globalStore.presetsHasUnsavedChangesPresent;
  }

  @Watch("unsavedChangesPresent", { immediate: true })
  handleUnsavedChangesPresentChange(unsavedChangesPresent: boolean) {
    if (unsavedChangesPresent) {
      this.requestPanelClosingConfirmation();
    } else {
      uiStore.noClosingConfirmationIsNeededFor("presets");
    }
  }

  get confirmationChosenOption(): ClosingConfirmationChosenOption | undefined {
    return uiStore.closingConfirmations.presets?.chosenOption;
  }

  @Watch("confirmationChosenOption", { immediate: true })
  async handleConfirmationChosenOptionChange(
    chosenOption: ClosingConfirmationChosenOption | undefined
  ) {
    if (chosenOption === "discard") {
      uiStore.setClosingConfirmationIsDiscarding({
        subject: "globalSettings",
        isDiscarding: true
      });
      if (await this.load()) {
        this.close();
      } else {
        this.requestPanelClosingConfirmation();
      }
    } else if (chosenOption === "save") {
      uiStore.setClosingConfirmationIsSaving({
        subject: "globalSettings",
        isSaving: true
      });
      if (await this.save()) {
        this.close();
      } else {
        this.requestPanelClosingConfirmation();
      }
    }
  }

  get isLoading() {
    return this.isLoadingData || this.isStoringData;
  }

  close() {
    closeUiPanel("presets");
  }
}
